import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTheme, createTheme } from '@mui/material/styles';
import { Box, CircularProgress } from "@mui/material";

// Icons & Assets
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EmailIcon from '@mui/icons-material/Mail';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import SettingsIcon from '@mui/icons-material/Settings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import lightLogo from './assets/logo_light.png';
import darkLogo from './assets/logo_dark.png';

// Toolpad Core
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";

import usePermissions from "./hooks/usePermissions";


const customTheme = createTheme({
  cssVariables: { colorSchemeSelector: "data-toolpad-color-scheme" },
  palette: { mode: "light" },
  colorSchemes: { light: true, dark: true },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 600, lg: 1200, xl: 1536 },
  },
});

function BrandLogo() {
  const theme = useTheme();
  return (
    <img
      src={theme.palette.mode === 'dark' ? darkLogo : lightLogo}
      alt="Ultraviolette"
      style={{ height: 40 }}
    />
  );
}

// Helper to find the first accessible segment
const findFirstAccessiblePath = (navItems) => {
  for (const item of navItems) {
    if (item.children) {
      const childPath = findFirstAccessiblePath(item.children);
      if (childPath) return `${item.segment}/${childPath}`;
    } else if (item.segment) {
      return item.segment;
    }
  }
  return null;
};

function Dashboard({ window, onSignOut, sessionData }) {
  const navigate = useNavigate();
  const location = useLocation();


  const [session, setSession] = useState({
    user: {
      name: sessionData.name,
      email: sessionData.email,
    },
  });

  const { permissions, loading } = usePermissions();

  const authentication = useMemo(
    () => ({
      signIn: () => navigate("/signin"),
      signOut: () => {
        localStorage.removeItem("accessToken");
        setSession(null);
        onSignOut();
        navigate("/signin");
      },
    }),
    [navigate, onSignOut]
  );

  const router = useMemo(() => ({
    pathname: location.pathname,
    searchParams: new URLSearchParams(location.search),
    navigate: (path) => navigate(path),
  }), [location, navigate]);

  const navigation = useMemo(() => [
    ...(permissions.awards || permissions.topNews || permissions.stories ? [{
      segment: "press-page",
      title: "Press Page",
      icon: <NewspaperIcon />,
      children: [
        ...(permissions.topNews ? [{ segment: "topnews", title: "Top News" }] : []),
        ...(permissions.stories ? [{ segment: "stories", title: "Stories" }] : []),
        ...(permissions.awards ? [{ segment: "awards", title: "Awards" }] : []),
      ]
    }] : []),
    ...(permissions.dealer ? [{
      segment: "dealer/dealership-master",
      title: "Dealership Master",
      icon: <AddBusinessIcon />,
    }] : []),
    ...(permissions.service ? [{
      segment: "service/service-centre-master",
      title: "Service Centre Master",
      icon: <SettingsIcon />,
    }] : []),
    ...(permissions.mailTemplates ? [{
      segment: "mail-templates",
      title: "Mail Templates",
      icon: <EmailIcon />,
    }] : []),
    ...(permissions.tagManagement ? [{
      segment: "tag-management",
      title: "Tag Management",
      icon: <LocalOfferIcon />,
    }] : []),
    ...(permissions.superAdmin || permissions.flightScanUsers || permissions.odoMateUsers ? [{
      segment: "manageusers",
      title: "Manage Users",
      icon: <ManageAccountsIcon />,
      children: [
        ...(permissions.superAdmin ? [{ segment: "cmsusers", title: "CMS Users" }] : []),
        ...(permissions.flightScanUsers ? [{ segment: "flightscanusers", title: "Flightscan Users" }] : []),
        ...(permissions.odoMateUsers ? [{ segment: "odomateusers", title: "OdoMate Users" }] : []),
      ]
    }] : []),
    ...(permissions.homeTestRide ? [{
      segment: "home-test-ride",
      title: "Home Test Ride",
      icon: <TwoWheelerIcon />,
      children: [
        { segment: "reservation", title: "Reservation" },
        { segment: "offdays", title: "Offdays" },
        { segment: "upcoming-bookings", title: "Upcoming Bookings" },
      ]
    }] : []),
    ...(permissions.wallpaper ? [{
      segment: "wallpaper",
      title: "Wallpaper",
      icon: <InsertPhotoIcon />,
    }] : []),
    ...(permissions.uvMissions ? [{
      segment: "uv-missions",
      title: "UV Missions",
      icon: <AssignmentIcon />,
    }] : []),
    ...(permissions.notifications ? [{
      segment: "notifications",
      title: "Notifications",
      icon: <NotificationsIcon />,
    }] : []),
  ], [permissions]);

  useEffect(() => {
    // Only redirect if we are at the root of the dashboard
    // and we have loaded permissions (assumed loaded if navigation is populated)
    if (!loading && (location.pathname === '/' || location.pathname === '/dashboard')) {
      const firstPath = findFirstAccessiblePath(navigation);
      if (firstPath) {
        navigate(firstPath, { replace: true });
      }
    }
  }, [navigation, location.pathname, navigate, loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppProvider
      session={session}
      authentication={authentication}
      branding={{
        logo: <BrandLogo />,
        title: "",
      }}
      navigation={navigation}
      router={router}
      theme={customTheme}
      window={window !== undefined ? window() : undefined}
    >
      <DashboardLayout>
        <Box
          sx={{
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%"
          }}
        >
          <Outlet />
        </Box>
      </DashboardLayout>
    </AppProvider>
  );
}

Dashboard.propTypes = {
  window: PropTypes.func,
  onSignOut: PropTypes.func.isRequired,
  sessionData: PropTypes.object.isRequired,
};

export default Dashboard;
